import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { useLeague } from "./LeagueLayout";

const TYPE_META = {
  real_team_transfer: { emoji: "🔁", label: "Trasferimento reale", cls: "bg-cyan-500/15 text-cyan-300 border-cyan-500/40" },
  listing_transition: { emoji: "📋", label: "Cambio listing", cls: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
};

const STATUS_LABEL = {
  LISTED_ACTIVE: { emoji: "✅", cls: "text-emerald-300" },
  LISTED_CEDUTO: { emoji: "🏳️", cls: "text-orange-300" },
  TEMPORARILY_UNLISTED: { emoji: "🚫", cls: "text-slate-400" },
  RELISTED: { emoji: "✨", cls: "text-emerald-400" },
};

export default function LeagueDiarioMercato() {
  const { league, isAdmin } = useLeague();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | real_team_transfer | listing_transition
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/leagues/${league.league_id}/diario-mercato?limit=500`);
        setEvents(r.data.events || []);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [league.league_id]);

  const filtered = events
    .filter(e => filter === "all" || e.type === filter)
    .filter(e => !search || (e.name || "").toLowerCase().includes(search.toLowerCase()));

  if (!isAdmin) {
    return <div className="text-slate-500 py-10 text-center">Solo l&apos;admin può accedere al Diario Mercato.</div>;
  }

  return (
    <div className="space-y-4">
      <div>
        <div className="text-xs tracking-[0.3em] text-cyan-400 uppercase font-bold">📓 Diario Mercato</div>
        <h2 className="text-3xl font-black tracking-tight">Cronologia trasferimenti reali & listing</h2>
        <p className="text-slate-400 text-sm mt-1">Ogni cambio di squadra Serie A e ogni transizione di stato listone rilevata dagli import.</p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex rounded-2xl border border-slate-800 overflow-hidden">
          {["all", "real_team_transfer", "listing_transition"].map(t => (
            <button key={t} onClick={() => setFilter(t)} data-testid={`diario-filter-${t}`}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider ${filter === t ? "bg-cyan-500/20 text-cyan-300" : "text-slate-500 hover:text-slate-300"}`}>
              {t === "all" ? "Tutti" : (TYPE_META[t]?.label || t)}
            </button>
          ))}
        </div>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca giocatore…" data-testid="diario-search"
          className="flex-1 min-w-[200px] bg-slate-950 border border-slate-800 rounded-2xl px-3 py-1.5 text-sm text-slate-100 placeholder:text-slate-600"/>
        <div className="text-xs text-slate-400">
          <strong className="text-cyan-300">{filtered.length}</strong> / {events.length} eventi
        </div>
      </div>

      {loading ? (
        <div className="text-slate-500 py-10 text-center">Caricamento…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-slate-800 rounded-3xl p-10 text-center text-slate-500">
          <div className="text-4xl mb-2">📭</div>
          Nessun evento registrato ancora. Verrà popolato ai prossimi import listone.
        </div>
      ) : (
        <div className="border border-slate-800 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 overflow-hidden shadow-2xl shadow-black/40">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-950/70">
                <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500">
                  <th className="px-3 py-3">Tipo</th>
                  <th className="px-3 py-3">Giocatore</th>
                  <th className="px-3 py-3">Da</th>
                  <th className="px-3 py-3">A</th>
                  <th className="px-3 py-3">Season</th>
                  <th className="px-3 py-3">Data</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e, i) => {
                  const meta = TYPE_META[e.type] || TYPE_META.listing_transition;
                  const fromStatus = STATUS_LABEL[e.from];
                  const toStatus = STATUS_LABEL[e.to];
                  return (
                    <tr key={`${e.fanta_id}-${i}`} className="border-t border-slate-800 hover:bg-slate-800/30 transition-colors" data-testid={`diario-row-${i}`}>
                      <td className="px-3 py-2.5">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold border ${meta.cls}`}>
                          {meta.emoji} {meta.label}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 font-bold text-slate-100">
                        {e.name}
                        <span className="text-[10px] text-slate-500 font-mono ml-1.5">#{e.fanta_id}</span>
                      </td>
                      <td className="px-3 py-2.5">
                        {fromStatus ? <span className={`font-mono ${fromStatus.cls}`}>{fromStatus.emoji} {e.from}</span> : <span className="text-slate-300">{e.from || "—"}</span>}
                      </td>
                      <td className="px-3 py-2.5">
                        {toStatus ? <span className={`font-mono font-bold ${toStatus.cls}`}>{toStatus.emoji} {e.to}</span> : <span className="text-slate-100 font-bold">{e.to || "—"}</span>}
                      </td>
                      <td className="px-3 py-2.5 text-xs text-slate-400 font-mono">{e.season || "—"}</td>
                      <td className="px-3 py-2.5 text-xs text-slate-400 font-mono">{e.at ? new Date(e.at).toLocaleString("it-IT") : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
